import { TruckOutlined } from '../../components/icons'
import { Checkbox } from '../../components/ui/Checkbox'
import type { components } from '../../lib/api/schema.gen'
import { AddLineForm } from './AddLineForm'
import { CartLineRow } from './CartLineRow'
import type { CartLineConfiguratorService } from './cart-line-configurator'
import { deriveShipmentGroups } from './ship-as-ready-breakdown'
import type { ShipmentGroup } from './ship-as-ready-breakdown'

type LineItem = components['schemas']['LineItem']
type CartLinePresentation = components['schemas']['CartLinePresentation']
type CartLineSpecification = components['schemas']['CartLineSpecification']
type CartViewWithLineSpecifications = components['schemas']['CartViewWithLineSpecifications']
type ShipMode = components['schemas']['ShipMode']
type Sourceable = components['schemas']['Sourceable']
type Quantity = components['schemas']['Quantity']

export interface CartLineTableProps {
  readonly lines: readonly LineItem[]
  readonly presentationsBySourceableId: ReadonlyMap<string, CartLinePresentation>
  /** UID-434/WS-E — each line's PERSISTED manufacturing configuration
   * (`CartViewWithLineSpecifications.lineSpecifications`), joined by
   * sourceable id — threaded to each row's "Edit configuration" shell. */
  readonly specificationsBySourceableId: ReadonlyMap<string, CartLineSpecification>
  /** UID-411 — each line's real, resolved part thumbnail
   * (`CartViewWithLineSpecifications.lineThumbnails`), joined by sourceable id;
   * a line with no entry falls back to the existing placeholder glyph. */
  readonly thumbnailsBySourceableId: ReadonlyMap<string, string>
  readonly selectedIds: ReadonlySet<string>
  readonly busyLineIds: ReadonlySet<string>
  /** Passed to each row: when true, reordered lines show a per-line "From order …"
   * marker. CartPage sets this only when the cart mixes 2+ distinct source orders
   * (UID-94), so the single-reorder case relies on the cart-level notice alone. */
  readonly markSourceOrder?: boolean
  /** UID-96 (C3) / UID-291 — the cart's ship-plan mode. In `ship_as_ready`
   * mode this ALSO drives the in-table shipment-bucket headers (see the
   * component doc below); in `ship_together` mode it's threaded down to
   * every row's `deriveLineDateDisplay` so a line ready earlier than the
   * order-level date shows its own date struck through. Defaults match
   * `CartLineRow`'s own defaults (ship-as-ready, no strike-through) so
   * existing callers/tests that don't pass these are unaffected. */
  readonly mode?: ShipMode | undefined
  readonly orderShipDate?: string | null | undefined
  readonly orderShipDateLabel?: string | null | undefined
  /** UID-610 — `shiftKey` (not `selected`) reaches here from each row's
   * checkbox `onClick`. The caller resolves it into a Gmail/Finder range
   * select via the shared `useRangeSelection` hook — see `CartPage.tsx`'s
   * `toggleSelected`. */
  readonly onToggleSelected: (sourceableId: string, shiftKey: boolean) => void
  readonly onToggleSelectAll: (selected: boolean) => void
  readonly onQuantityChange: (sourceableId: string, nextValue: number) => void
  readonly onRemove: (sourceableId: string) => void
  /** UID-434/WS-E — called with the FULL next cart once a row's "Edit
   * configuration" shell persists a quantity/specification change. */
  readonly onConfigurationSaved: (cart: CartViewWithLineSpecifications) => void
  /** Injectable for tests; forwarded to every row. */
  readonly configuratorService?: CartLineConfiguratorService
  /** The inline "Add parts" row (design-fidelity: it lives INSIDE this table
   * card, below the column header and above the line items — see the
   * rendered design). CartPage toggles `showAddRow` from the header "Add
   * parts" button; `onAddLine` resolves+POSTs the typed SKU; `onCancelAddRow`
   * closes the row (the ✕). All optional so the table's own tests (which
   * don't exercise the add row) can omit them. */
  readonly showAddRow?: boolean
  readonly onAddLine?: (sourceable: Sourceable, quantity: Quantity) => Promise<void>
  readonly onCancelAddRow?: () => void
}

/**
 * The Cart screen's line-item table (UID-291 — corrects UID-270's grouped
 * "Individual parts" + named custom/BOM group sections, which do not exist
 * in the authoritative prototype, `Fictiv Platform.dc.html`'s `isCart`
 * screen / `cartRows` view-model): a select-all header checkbox +
 * PART/REQUIREMENTS/SHIP DATE/QTY/SUBTOTAL columns, then the lines
 * themselves, rendered per the cart's ship-plan mode:
 *
 *  - **Ship together** (`mode !== 'ship_as_ready'`): one flat list of
 *    `CartLineRow`s under the single shared column header — no section
 *    headers, no chevrons, no per-section subtotal (matches the
 *    prototype's `cartRows = cartLines.map(cl => ({...cl, isHeader:false,
 *    isRow:true}))`, `Fictiv Platform.dc.html` ~L9057).
 *  - **Ship as ready** (`mode === 'ship_as_ready'`): the ONLY in-table
 *    sectioning the prototype has — a header per readiness bucket
 *    (standard → configurable → custom, whichever are present), each
 *    reading "Arrives {date} · Shipment N of M · N parts · M units ·
 *    {subtotal}" and tinted per bucket (`_shipHdrMeta`,
 *    `Fictiv Platform.dc.html` ~L9048-9057 / template ~L2208-2216), followed
 *    by that bucket's own lines as plain rows. Bucket membership/ordering
 *    comes from `deriveShipmentGroups` (`ship-as-ready-breakdown.ts`) — the
 *    SAME derivation `ShipmentBreakdown`/`ShipPlanCard` use for the
 *    delivery-option cards above this table, so there is one source of
 *    truth for "which bucket is a line in, and in what order" even though
 *    the cards (delivery-date/region selection) and these in-table headers
 *    (the line list itself) are two different renderings — exactly what the
 *    prototype itself does (its `shipCard.splitCards` cards AND its
 *    `cartRows` bucket headers both exist simultaneously).
 *
 * Group identity (BOM/custom group name + its own subtotal) is NOT shown
 * here — the prototype puts that in the order-summary sidebar's "Separate
 * POs per group" / `poBreakdown` card, not the line table (UID-274 keeps the
 * `groupId`/`groupName`/`groupKind` contract fields available for that
 * purpose; this table no longer reads them).
 *
 * When `showAddRow` is set, an inline add-parts row renders below the
 * header and above the lines (design-fidelity: the add control lives
 * inside this card, not as a separate panel above it). Otherwise purely
 * presentational — bulk actions (the "N selected · Remove selected" bar)
 * live in `CartPage`, which owns the selection state this table only
 * reflects.
 */
export function CartLineTable({
  lines,
  presentationsBySourceableId,
  specificationsBySourceableId,
  thumbnailsBySourceableId,
  selectedIds,
  busyLineIds,
  markSourceOrder = false,
  mode,
  orderShipDate,
  orderShipDateLabel,
  onToggleSelected,
  onToggleSelectAll,
  onQuantityChange,
  onRemove,
  onConfigurationSaved,
  configuratorService,
  showAddRow = false,
  onAddLine,
  onCancelAddRow,
}: CartLineTableProps) {
  // Checkbox is a binary primitive (no indeterminate state) — a partial
  // selection renders the select-all box simply unchecked rather than
  // faking a third visual state the primitive doesn't support.
  const allSelected = lines.length > 0 && lines.every((line) => selectedIds.has(line.sourceable.id))
  // Bundled once per render — every row needs the same selection/busy state
  // and callbacks.
  const rowProps = {
    presentationsBySourceableId,
    specificationsBySourceableId,
    thumbnailsBySourceableId,
    selectedIds,
    busyLineIds,
    markSourceOrder,
    mode,
    orderShipDate,
    orderShipDateLabel,
    onToggleSelected,
    onQuantityChange,
    onRemove,
    onConfigurationSaved,
    configuratorService,
  }
  const shipmentGroups =
    mode === 'ship_as_ready' ? deriveShipmentGroups(lines, presentationsBySourceableId) : []

  return (
    <table className="cart-line-table">
      <caption className="sr-only">Cart line items</caption>
      <thead>
        <tr>
          <th scope="col" className="cart-line-select-cell">
            <Checkbox
              checked={allSelected}
              onChange={onToggleSelectAll}
              label={<span className="sr-only">Select all cart lines</span>}
            />
          </th>
          {/* Part is the flexible column (no width class) — it absorbs the
              table's leftover space; the other columns carry their sizing class
              so `table-layout:fixed` picks up their widths from this header row. */}
          <th scope="col">Part</th>
          <th scope="col" className="cart-line-requirements-cell">
            Requirements
          </th>
          <th scope="col" className="cart-line-ship-date-cell">
            Ship date
          </th>
          <th scope="col" className="cart-line-qty-cell">
            Qty
          </th>
          <th scope="col" className="cart-line-subtotal-cell">
            Subtotal
          </th>
          <th scope="col" className="cart-line-actions-cell">
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      {showAddRow && onAddLine && (
        <tbody>
          <tr className="cart-add-line-row">
            {/* Spans every column: select + Part + Requirements + Ship date +
                Qty + Subtotal + Actions = 7. The flex layout lives on an inner
                <div>, NOT the <td>: `display:flex` on a table cell drops its
                table-cell behavior, so `colSpan` is ignored and the cell
                collapses to its content width. */}
            <td className="cart-add-line-td" colSpan={7}>
              <div className="cart-add-line-cell">
                <span className="cart-add-line-icon" aria-hidden="true">
                  <TagIcon />
                </span>
                <AddLineForm variant="in-cart-row" onAdd={onAddLine} />
                {onCancelAddRow && (
                  <button
                    type="button"
                    className="cart-add-line-close"
                    aria-label="Cancel adding a part"
                    onClick={onCancelAddRow}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      )}
      {mode === 'ship_as_ready' ? (
        shipmentGroups.map((group, index) => (
          <tbody key={group.bucket}>
            <ShipBucketHeaderRow group={group} ordinal={index + 1} total={shipmentGroups.length} />
            {group.lines.map((line) => (
              <CartLineTableRow key={line.sourceable.id} line={line} rowProps={rowProps} />
            ))}
          </tbody>
        ))
      ) : (
        <tbody>
          {lines.map((line) => (
            <CartLineTableRow key={line.sourceable.id} line={line} rowProps={rowProps} />
          ))}
        </tbody>
      )}
    </table>
  )
}

/**
 * A "ship as ready" bucket header — one per readiness bucket present,
 * ordered standard → configurable → custom (the order `deriveShipmentGroups`
 * already returns them in). Spans every column, above that bucket's own
 * rows, tinted per bucket via `data-bucket` (`.cart-ship-header-row` in
 * `cart.css`, reusing the same `--color-bucket-*` tokens as
 * `ShipmentBreakdown`'s bucket badges — one palette, two renderings).
 * "Shipment N of M" (M = buckets actually present, not a fixed 3) matches
 * the prototype's `hdrShip` field verbatim (`Fictiv Platform.dc.html`
 * `'Shipment '+sn+' of '+nShip`).
 */
function ShipBucketHeaderRow({
  group,
  ordinal,
  total,
}: {
  readonly group: ShipmentGroup
  readonly ordinal: number
  readonly total: number
}) {
  return (
    <tr className="cart-ship-header-row" data-bucket={group.bucket}>
      <td className="cart-ship-header-td" colSpan={7}>
        <div className="cart-ship-header">
          <span className="cart-ship-header-arrive">
            <TruckOutlined aria-hidden="true" />
            {group.arriveLabel}
          </span>
          <span className="cart-ship-header-shipment">
            Shipment {ordinal} of {total}
          </span>
          <span className="cart-ship-header-spacer" />
          <span className="cart-ship-header-count">
            {group.lineCount} {group.lineCount === 1 ? 'part' : 'parts'} · {group.unitCount}{' '}
            {group.unitCount === 1 ? 'unit' : 'units'}
          </span>
          <span className="cart-ship-header-total">{group.subtotalLabel}</span>
        </div>
      </td>
    </tr>
  )
}

/** One `CartLineRow`, wired to the shared per-table callbacks/state bundle
 * (`rowProps`) — factored out so both the flat (ship-together) list and the
 * per-bucket (ship-as-ready) lists render a row identically. */
function CartLineTableRow({
  line,
  rowProps,
}: {
  readonly line: LineItem
  readonly rowProps: {
    readonly presentationsBySourceableId: ReadonlyMap<string, CartLinePresentation>
    readonly specificationsBySourceableId: ReadonlyMap<string, CartLineSpecification>
    readonly thumbnailsBySourceableId: ReadonlyMap<string, string>
    readonly selectedIds: ReadonlySet<string>
    readonly busyLineIds: ReadonlySet<string>
    readonly markSourceOrder: boolean
    readonly mode: ShipMode | undefined
    readonly orderShipDate: string | null | undefined
    readonly orderShipDateLabel: string | null | undefined
    readonly onToggleSelected: (sourceableId: string, shiftKey: boolean) => void
    readonly onQuantityChange: (sourceableId: string, nextValue: number) => void
    readonly onRemove: (sourceableId: string) => void
    readonly onConfigurationSaved: (cart: CartViewWithLineSpecifications) => void
    readonly configuratorService: CartLineConfiguratorService | undefined
  }
}) {
  return (
    <CartLineRow
      line={line}
      presentation={rowProps.presentationsBySourceableId.get(line.sourceable.id)}
      specification={rowProps.specificationsBySourceableId.get(line.sourceable.id)}
      thumbnailUrl={rowProps.thumbnailsBySourceableId.get(line.sourceable.id)}
      selected={rowProps.selectedIds.has(line.sourceable.id)}
      onToggleSelected={(event) => rowProps.onToggleSelected(line.sourceable.id, event.shiftKey)}
      busy={rowProps.busyLineIds.has(line.sourceable.id)}
      showSourceOrder={rowProps.markSourceOrder}
      {...(rowProps.mode !== undefined ? { mode: rowProps.mode } : {})}
      {...(rowProps.orderShipDate !== undefined ? { orderShipDate: rowProps.orderShipDate } : {})}
      {...(rowProps.orderShipDateLabel !== undefined
        ? { orderShipDateLabel: rowProps.orderShipDateLabel }
        : {})}
      onQuantityChange={(nextValue) => rowProps.onQuantityChange(line.sourceable.id, nextValue)}
      onRemove={() => rowProps.onRemove(line.sourceable.id)}
      onConfigurationSaved={rowProps.onConfigurationSaved}
      {...(rowProps.configuratorService
        ? { configuratorService: rowProps.configuratorService }
        : {})}
    />
  )
}

/** Decorative tag/label glyph in the inline add-parts row's leading box
 * (matches the rendered design's part-number affordance). */
function TagIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <circle cx="7" cy="7" r="1.2" />
    </svg>
  )
}
